import { Box, Button, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { ChangelogSince } from "@xop/dashboard-shared";
import type { JSX } from "react";

import { formatDay } from "./dateHelpers.js";

/**
 * The "What's new since you upgraded" banner shown in since-mode (deep-linked
 * after a desktop upgrade). Names the version/date and offers an exit back to
 * month browsing. Extracted from `ChangelogPanel.tsx`.
 */
export function WhatsNewBanner({
  since,
  count,
  loading,
  onExit,
}: {
  since: ChangelogSince | null;
  count: number;
  loading: boolean;
  onExit: () => void;
}): JSX.Element {
  const heading = since?.version
    ? `What's new since v${since.version}`
    : since?.date
      ? `What's new since ${formatDay(since.date)}`
      : "What's new";
  const sub =
    since?.version && !since.date
      ? "Couldn't pinpoint that version — showing everything across the stack."
      : `${count} update${count === 1 ? "" : "s"} across the stack since then.`;
  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1.5,
        borderRadius: 1,
        border: 1,
        borderColor: "primary.main",
        bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
        px: 2,
        py: 1.5,
      }}
    >
      <Box sx={{ display: "grid", gap: 0.25 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: "text.primary" }}>
          {heading}
        </Typography>
        <Typography sx={{ fontSize: 12, color: "text.disabled" }}>
          {loading ? "Loading…" : sub}
        </Typography>
      </Box>
      <Button variant="outlined" color="inherit" size="small" onClick={onExit}>
        Browse by month
      </Button>
    </Box>
  );
}
